import { useEffect, useMemo, useState, useCallback } from 'react';
import { api, UnauthorizedError, type Account, type Row, type AuthUser } from './api';
import { DataTable, type TableState } from './components/DataTable';
import { BarChartView } from './components/BarChartView';
import { LoginGate } from './components/LoginGate';
import { QuickView } from './components/QuickView';
import { ServiceKeys } from './components/ServiceKeys';
import { Users } from './components/Users';
import { AuditLog } from './components/AuditLog';
import { WhatsAppChats } from './components/WhatsAppChats';
import { EvolutionApi } from './components/EvolutionApi';
import { downloadCsv } from './lib/format';
import { periodRanges, type Mode, type Period } from './lib/periods';

type Section = 'ads' | 'whatsapp' | 'evolution';
type Dataset = 'campaign' | 'adset' | 'ad' | 'utm' | 'creatives';
type View = 'table' | 'chart';

const DATASET_COLUMNS: Record<Dataset, string[]> = {
  campaign: [
    'campaign_name',
    'date_start',
    'spend',
    'impressions',
    'clicks',
    'ctr',
    'cpc',
    'cpm',
    'conversions',
    'cpa',
    'roas',
    'conversion_rate',
  ],
  adset: [
    'campaign_name',
    'adset_name',
    'date_start',
    'spend',
    'impressions',
    'clicks',
    'ctr',
    'cpc',
    'cpm',
    'conversions',
    'cpa',
    'roas',
  ],
  ad: [
    'campaign_name',
    'adset_name',
    'ad_name',
    'date_start',
    'spend',
    'impressions',
    'clicks',
    'ctr',
    'cpc',
    'cpm',
    'conversions',
    'cpa',
    'roas',
  ],
  utm: [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'ad_name',
    'date_start',
    'spend',
    'clicks',
    'ctr',
    'cpc',
    'conversions',
    'cpa',
    'roas',
  ],
  creatives: [
    'creative_name',
    'ad_name',
    'campaign_name',
    'date_start',
    'spend',
    'impressions',
    'clicks',
    'ctr',
    'cpc',
    'conversions',
    'cpa',
    'roas',
  ],
};

const METRIC_OPTIONS: Array<{ key: keyof Row; label: string }> = [
  { key: 'spend', label: 'Gasto' },
  { key: 'impressions', label: 'Impressões' },
  { key: 'clicks', label: 'Cliques' },
  { key: 'ctr', label: 'CTR' },
  { key: 'cpc', label: 'CPC' },
  { key: 'cpm', label: 'CPM' },
  { key: 'conversions', label: 'Conversões' },
  { key: 'cpa', label: 'CPA' },
  { key: 'roas', label: 'ROAS' },
  { key: 'conversion_rate', label: 'Conv. Rate' },
];

type AuthStatus = 'loading' | 'in' | 'out';

export default function App() {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    // Pick up an error string the worker passed via ?auth_error= in the redirect.
    const params = new URLSearchParams(window.location.search);
    const err = params.get('auth_error');
    if (err) {
      setAuthError(decodeURIComponent(err));
      // Clean it from the URL so refresh doesn't re-show.
      window.history.replaceState({}, '', window.location.pathname);
    }
    api
      .me()
      .then((r) => {
        setUser(r.user);
        setStatus('in');
      })
      .catch((e) => {
        if (!(e instanceof UnauthorizedError)) setAuthError(String(e.message || e));
        setStatus('out');
      });
  }, []);

  const onLogout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
      setStatus('out');
    }
  }, []);

  const onSessionExpired = useCallback(() => {
    setUser(null);
    setAuthError('Sessão expirada. Faça login novamente.');
    setStatus('out');
  }, []);

  if (status === 'loading') {
    return (
      <div className="login-gate">
        <div className="login-card" style={{ textAlign: 'center', color: 'var(--muted)' }}>
          carregando…
        </div>
      </div>
    );
  }
  if (status === 'out' || !user) {
    return <LoginGate error={authError} />;
  }
  return <Dashboard user={user} onLogout={onLogout} onSessionExpired={onSessionExpired} />;
}

type DashboardProps = {
  user: AuthUser;
  onLogout: () => void;
  onSessionExpired: () => void;
};

function Dashboard({ user, onLogout, onSessionExpired }: DashboardProps) {
  const [section, setSection] = useState<Section>('ads');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountId, setAccountId] = useState('');
  const [dataset, setDataset] = useState<Dataset>('campaign');
  const [period, setPeriod] = useState<Period>('week');
  const [mode, setMode] = useState<Mode>('rolling');
  const { since, until } = useMemo(() => periodRanges(period, mode).current, [period, mode]);
  const [view, setView] = useState<View>('table');
  const [chartDimOverride, setChartDimOverride] = useState<keyof Row | ''>('');
  const [chartMetric, setChartMetric] = useState<keyof Row>('spend');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cached, setCached] = useState(false);

  const [showKeys, setShowKeys] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isAdmin = user.role === 'admin';

  const closeMenu = () => setMenuOpen(false);
  const goToSection = (s: Section) => {
    setSection(s);
    closeMenu();
  };
  const openModal = (set: (v: boolean) => void) => {
    set(true);
    closeMenu();
  };

  const [tableState, setTableState] = useState<TableState>({
    sorting: [{ id: 'spend', desc: true }],
    filters: [],
    grouping: [],
    expanded: {},
    globalFilter: '',
  });

  // Load accounts on mount
  useEffect(() => {
    api
      .accounts()
      .then((r) => {
        setAccounts(r.data);
        if (r.data.length > 0 && !accountId) setAccountId(r.data[0].id);
      })
      .catch((e) => {
        if (e instanceof UnauthorizedError) onSessionExpired();
        else setError(String(e.message || e));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      if (dataset === 'campaign' || dataset === 'adset' || dataset === 'ad') {
        const r = await api.insights({ account_id: accountId, level: dataset, since, until });
        setRows(r.data);
        setCached(r.cached);
      } else if (dataset === 'utm') {
        const r = await api.utm({ account_id: accountId, since, until });
        setRows(r.data);
        setCached(r.cached);
      } else if (dataset === 'creatives') {
        const r = await api.creatives({ account_id: accountId, since, until });
        setRows(r.data);
        setCached(r.cached);
      }
      setTableState((s) => ({ ...s, expanded: {}, grouping: defaultGrouping(dataset) }));
    } catch (e: any) {
      if (e instanceof UnauthorizedError) {
        onSessionExpired();
      } else {
        setError(String(e.message || e));
      }
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [accountId, dataset, since, until, onSessionExpired]);

  useEffect(() => {
    if (accountId) load();
  }, [accountId, dataset, since, until, load]);

  const cols = useMemo(() => DATASET_COLUMNS[dataset], [dataset]);

  // Chart dimension = deepest table grouping column unless user picks another.
  // Falls back to the dataset's primary dimension if no grouping is active.
  const chartDimOptions = useMemo<Array<{ key: keyof Row; label: string }>>(() => {
    const fromGrouping = tableState.grouping.map((g) => ({
      key: g as keyof Row,
      label: LABELS[g] || g,
    }));
    if (fromGrouping.length > 0) return fromGrouping;
    return fallbackDimensions(dataset);
  }, [tableState.grouping, dataset]);

  const chartDim: keyof Row =
    chartDimOverride && chartDimOptions.some((o) => o.key === chartDimOverride)
      ? chartDimOverride
      : chartDimOptions[chartDimOptions.length - 1]?.key ?? 'campaign_name';

  const onExport = () => {
    const headerLabels: Record<string, string> = {
      account_name: 'Conta',
      campaign_name: 'Campanha',
      adset_name: 'Ad Set',
      ad_name: 'Anúncio',
      creative_name: 'Criativo',
      utm_source: 'utm_source',
      utm_medium: 'utm_medium',
      utm_campaign: 'utm_campaign',
      utm_content: 'utm_content',
      utm_term: 'utm_term',
      spend: 'Gasto',
      impressions: 'Impressões',
      clicks: 'Cliques',
      link_clicks: 'Link clicks',
      ctr: 'CTR (%)',
      cpc: 'CPC',
      cpm: 'CPM',
      conversions: 'Conversões',
      cpa: 'CPA',
      roas: 'ROAS',
      conversion_rate: 'Conv. Rate (%)',
    };
    const fname = `spire-${dataset}-${since}_${until}.csv`;
    downloadCsv(fname, rows, cols, headerLabels);
  };

  const clearCache = async () => {
    await api.clearCache();
    await load();
  };

  const sectionTitle =
    section === 'ads'
      ? 'Meta Ads'
      : section === 'whatsapp'
      ? 'Conversas WhatsApp'
      : 'Evolution API';

  return (
    <div className="app" data-testid="app">
      <header className="header">
        <div className="header-left">
          <button
            className="hamburger"
            onClick={() => setMenuOpen(true)}
            aria-label="Abrir menu"
            data-testid="menu-toggle"
          >
            <span />
            <span />
            <span />
          </button>
          <h1
            className="brand"
            onClick={() => setSection('ads')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSection('ads');
              }
            }}
          >
            spire<span className="dot">.</span> dashboard
          </h1>
          <span className="header-section-pill">{sectionTitle}</span>
        </div>
        <div className="header-right">
          {section === 'ads' && (
            <div className="meta">{accounts.length} conta(s) Meta</div>
          )}
        </div>
      </header>

      {menuOpen && <NavDrawer
        user={user}
        section={section}
        isAdmin={isAdmin}
        onClose={closeMenu}
        onLogout={onLogout}
        onGo={goToSection}
        onOpenKeys={() => openModal(setShowKeys)}
        onOpenUsers={() => openModal(setShowUsers)}
        onOpenAudit={() => openModal(setShowAudit)}
      />}

      {section === 'whatsapp' ? (
        <main className="main">
          <WhatsAppChats onSessionExpired={onSessionExpired} isAdmin={isAdmin} />
        </main>
      ) : section === 'evolution' ? (
        <main className="main">
          <EvolutionApi onSessionExpired={onSessionExpired} isAdmin={isAdmin} />
        </main>
      ) : (
      <>
      <div className="toolbar" data-testid="toolbar">
        <div className="group">
          <label>Conta</label>
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            data-testid="account-select"
          >
            {accounts.length === 0 && <option value="">— carregando —</option>}
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.id})
              </option>
            ))}
          </select>
        </div>

        <div className="group">
          <label>Dataset</label>
          <select
            value={dataset}
            onChange={(e) => setDataset(e.target.value as Dataset)}
            data-testid="dataset-select"
          >
            <option value="campaign">Campanhas</option>
            <option value="adset">Ad Sets</option>
            <option value="ad">Anúncios</option>
            <option value="utm">Por UTM</option>
            <option value="creatives">Por Criativo</option>
          </select>
        </div>

        <div className="group">
          <label>Buscar</label>
          <input
            placeholder="texto livre…"
            value={tableState.globalFilter}
            onChange={(e) => setTableState((s) => ({ ...s, globalFilter: e.target.value }))}
            data-testid="global-search"
          />
        </div>

        <div className="group" style={{ marginLeft: 'auto' }}>
          <button
            className={`btn ${view === 'table' ? 'active' : ''}`}
            onClick={() => setView('table')}
            data-testid="view-table"
          >
            Tabela
          </button>
          <button
            className={`btn ${view === 'chart' ? 'active' : ''}`}
            onClick={() => setView('chart')}
            data-testid="view-chart"
          >
            Gráfico
          </button>
          <button className="btn" onClick={onExport} data-testid="export-csv" disabled={rows.length === 0}>
            Exportar CSV
          </button>
          <button className="btn" onClick={clearCache} data-testid="clear-cache" title="Limpa cache D1 e recarrega">
            ↻
          </button>
        </div>
      </div>

      <main className="main">
        {accountId && (
          <QuickView
            accountId={accountId}
            period={period}
            mode={mode}
            onPeriodChange={setPeriod}
            onModeChange={setMode}
            onSessionExpired={onSessionExpired}
          />
        )}

        <div className="status">
          {loading && <span className="pill">carregando…</span>}
          {!loading && !error && <span className="pill">{rows.length} linhas</span>}
          {cached && <span className="pill">cache hit</span>}
          {tableState.grouping.length > 0 && (
            <span className="pill">agrupado por {tableState.grouping.join(' › ')}</span>
          )}
        </div>

        {error && <div className="error" data-testid="error">{error}</div>}

        {view === 'chart' ? (
          <>
            <div className="toolbar" style={{ borderTop: '1px solid var(--line)', borderBottom: 'none' }}>
              <div className="group">
                <label>Empilhar por</label>
                <select
                  value={String(chartDim)}
                  onChange={(e) => setChartDimOverride(e.target.value as keyof Row)}
                  data-testid="chart-dim"
                >
                  {chartDimOptions.map((d) => (
                    <option key={String(d.key)} value={String(d.key)}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="group">
                <label>Métrica</label>
                <select
                  value={String(chartMetric)}
                  onChange={(e) => setChartMetric(e.target.value as keyof Row)}
                  data-testid="chart-metric"
                >
                  {METRIC_OPTIONS.map((m) => (
                    <option key={String(m.key)} value={String(m.key)}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="group" style={{ color: 'var(--muted)', fontSize: 12 }}>
                Eixo X = data · stacks seguem agrupamento da tabela
              </div>
            </div>
            <BarChartView
              rows={rows}
              dimensionKey={chartDim}
              metricKey={chartMetric}
              metricLabel={METRIC_OPTIONS.find((m) => m.key === chartMetric)?.label || ''}
            />
          </>
        ) : (
          <DataTable rows={rows} state={tableState} setState={setTableState} visibleColumns={cols} />
        )}
      </main>
      </>
      )}

      {showKeys && (
        <ServiceKeys onClose={() => setShowKeys(false)} onSessionExpired={onSessionExpired} />
      )}
      {showUsers && isAdmin && (
        <Users
          myEmail={user.email}
          onClose={() => setShowUsers(false)}
          onSessionExpired={onSessionExpired}
        />
      )}
      {showAudit && isAdmin && (
        <AuditLog
          onClose={() => setShowAudit(false)}
          onSessionExpired={onSessionExpired}
        />
      )}
    </div>
  );
}

function defaultGrouping(dataset: Dataset): string[] {
  if (dataset === 'adset') return ['campaign_name', 'adset_name'];
  if (dataset === 'ad') return ['campaign_name', 'adset_name', 'ad_name'];
  if (dataset === 'utm') return ['utm_source', 'utm_campaign'];
  if (dataset === 'creatives') return ['creative_name', 'ad_name'];
  if (dataset === 'campaign') return ['campaign_name'];
  return [];
}

const LABELS: Record<string, string> = {
  account_name: 'Conta',
  campaign_name: 'Campanha',
  adset_name: 'Ad Set',
  ad_name: 'Anúncio',
  creative_name: 'Criativo',
  utm_source: 'utm_source',
  utm_medium: 'utm_medium',
  utm_campaign: 'utm_campaign',
  utm_content: 'utm_content',
  utm_term: 'utm_term',
};

type NavDrawerProps = {
  user: AuthUser;
  section: Section;
  isAdmin: boolean;
  onClose: () => void;
  onLogout: () => void;
  onGo: (s: Section) => void;
  onOpenKeys: () => void;
  onOpenUsers: () => void;
  onOpenAudit: () => void;
};

function NavDrawer({
  user,
  section,
  isAdmin,
  onClose,
  onLogout,
  onGo,
  onOpenKeys,
  onOpenUsers,
  onOpenAudit,
}: NavDrawerProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="drawer-backdrop" onClick={onClose} data-testid="nav-drawer">
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-profile">
          {user.picture ? (
            <img
              src={user.picture}
              alt=""
              className="drawer-avatar"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="drawer-avatar drawer-avatar-fallback">
              {(user.name || user.email).slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="drawer-profile-meta">
            <div className="drawer-profile-name">{user.name || user.email}</div>
            <div className="drawer-profile-email">{user.email}</div>
            <span className={`drawer-role-badge drawer-role-${user.role}`}>
              {user.role === 'admin' ? 'admin' : 'membro'}
            </span>
          </div>
          <button className="drawer-close" onClick={onClose} aria-label="Fechar menu">
            ×
          </button>
        </div>

        <nav className="drawer-nav">
          <div className="drawer-section-label">Navegação</div>
          <button
            className={`drawer-item ${section === 'ads' ? 'active' : ''}`}
            onClick={() => onGo('ads')}
            data-testid="nav-ads"
          >
            <NavIcon name="ads" />
            Meta Ads
          </button>
          <button
            className={`drawer-item ${section === 'whatsapp' ? 'active' : ''}`}
            onClick={() => onGo('whatsapp')}
            data-testid="nav-whatsapp"
          >
            <NavIcon name="whatsapp" />
            Conversas WhatsApp
          </button>
          <button
            className={`drawer-item ${section === 'evolution' ? 'active' : ''}`}
            onClick={() => onGo('evolution')}
            data-testid="nav-evolution"
          >
            <NavIcon name="evolution" />
            Evolution API
          </button>

          <div className="drawer-section-label">Gestão</div>
          <button
            className="drawer-item"
            onClick={onOpenKeys}
            data-testid="nav-keys"
          >
            <NavIcon name="keys" />
            Chaves de API
          </button>
          {isAdmin && (
            <button
              className="drawer-item"
              onClick={onOpenUsers}
              data-testid="nav-users"
            >
              <NavIcon name="users" />
              Usuários
            </button>
          )}
          {isAdmin && (
            <button
              className="drawer-item"
              onClick={onOpenAudit}
              data-testid="nav-audit"
            >
              <NavIcon name="audit" />
              Auditoria
            </button>
          )}
        </nav>

        <div className="drawer-footer">
          <button
            className="drawer-item drawer-logout"
            onClick={onLogout}
            data-testid="nav-logout"
          >
            <NavIcon name="logout" />
            Sair
          </button>
        </div>
      </aside>
    </div>
  );
}

type NavIconName = 'ads' | 'whatsapp' | 'evolution' | 'keys' | 'users' | 'audit' | 'logout';

function NavIcon({ name }: { name: NavIconName }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  return (
    <span className="drawer-item-icon">
      {name === 'ads' && (
        <svg {...common}>
          <path d="M4 20V10" />
          <path d="M10 20V4" />
          <path d="M16 20v-7" />
          <path d="M22 20H2" />
        </svg>
      )}
      {name === 'whatsapp' && (
        <svg {...common}>
          <path d="M21 12a8.5 8.5 0 0 1-12.4 7.6L3 21l1.5-5.4A8.5 8.5 0 1 1 21 12Z" />
        </svg>
      )}
      {name === 'evolution' && (
        <svg {...common}>
          <path d="M9 7V3" />
          <path d="M15 7V3" />
          <path d="M7 7h10v5a5 5 0 0 1-10 0V7Z" />
          <path d="M12 17v4" />
        </svg>
      )}
      {name === 'keys' && (
        <svg {...common}>
          <circle cx="8" cy="15" r="3.5" />
          <path d="M10.5 12.5 21 2" />
          <path d="m17 6 3 3" />
          <path d="m14 9 2.5 2.5" />
        </svg>
      )}
      {name === 'users' && (
        <svg {...common}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      )}
      {name === 'audit' && (
        <svg {...common}>
          <path d="M9 3h7a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6" />
          <path d="M9 3v3h7" />
          <path d="M9 12h6" />
          <path d="M9 16h6" />
        </svg>
      )}
      {name === 'logout' && (
        <svg {...common}>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <path d="m16 17 5-5-5-5" />
          <path d="M21 12H9" />
        </svg>
      )}
    </span>
  );
}

function fallbackDimensions(dataset: Dataset): Array<{ key: keyof Row; label: string }> {
  if (dataset === 'utm') return [{ key: 'utm_source', label: 'utm_source' }];
  if (dataset === 'creatives') return [{ key: 'creative_name', label: 'Criativo' }];
  if (dataset === 'ad') return [{ key: 'ad_name', label: 'Anúncio' }];
  if (dataset === 'adset') return [{ key: 'adset_name', label: 'Ad Set' }];
  return [{ key: 'campaign_name', label: 'Campanha' }];
}
